import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStructuredData } from '@/hooks/useStructuredData';

const TERMS_SCHEMAS = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms & Conditions — Onetvservices',
    url: 'https://onetvservices.com/terms',
    description: 'Onetvservices Terms & Conditions — subscription rules, acceptable use policy, refund eligibility, and legal disclaimers for our IPTV streaming service.',
    dateModified: '2026-04-03',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://onetvservices.com' },
        { '@type': 'ListItem', position: 2, name: 'Terms & Conditions', item: 'https://onetvservices.com/terms' },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: 'Onetvservices',
      url: 'https://onetvservices.com',
    },
  },
];

const SECTION_NUM = (n: number) => (
  <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold flex-shrink-0">
    {n}
  </span>
);

export default function TermsConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useStructuredData(TERMS_SCHEMAS);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-white/10 py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg" />
            <span className="text-sm">Back to Home</span>
          </Link>
          <div className="w-px h-5 bg-white/20" />
          <Link to="/">
            <img
              src="https://storage.readdy-site.link/project_files/ea087323-c332-4d1a-a768-642201e38806/8e03886a-a466-4209-8fc0-30625673693c_ONETV.png?v=410354e2b00bf17cf48639a5651635ec"
              alt="Onetvservices Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Title */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 text-primary text-sm font-medium mb-6">
            <i className="ri-file-text-line" />
            Legal Document
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms &amp; Conditions</h1>
          <p className="text-white/50 text-sm">Last updated: April 3, 2026</p>
        </div>

        <div className="space-y-10 text-white/80 leading-relaxed">

          {/* Intro */}
          <section>
            <p className="text-lg">
              Welcome to <strong className="text-white">Onetvservices</strong>. By accessing or using our IPTV streaming service, you agree to be bound by these Terms &amp; Conditions. Please read them carefully before subscribing or using our platform.
            </p>
          </section>

          {/* ── IMPORTANT DISCLAIMER ── */}
          <section>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-xl flex-shrink-0 mt-0.5">
                  <i className="ri-alert-line text-amber-400 text-xl" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-amber-300 mb-2">Important Notice — Please Read</h2>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Onetvservices is an <strong className="text-white">independent IPTV reseller</strong>. We are <strong className="text-white">not affiliated with, endorsed by, or partnered with</strong> any television broadcaster, streaming platform, sports league, or content producer. We do not own, produce, or hold exclusive rights to any of the content accessible through our service. Content availability may vary and is subject to change without notice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 1 — Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(1)}
              Service Description
            </h2>
            <p className="mb-4">
              Onetvservices provides an IPTV streaming service that gives subscribers access to live television channels, video-on-demand (VOD) content, and replay features via internet connection. Our service includes:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {[
                { icon: 'ri-tv-2-line', text: '+25,000 live TV channels in HD/4K quality' },
                { icon: 'ri-film-line', text: '+70,000 VOD titles (movies & series)' },
                { icon: 'ri-football-line', text: 'Live sports events from various channels' },
                { icon: 'ri-global-line', text: 'International content from multiple regions' },
                { icon: 'ri-history-line', text: 'Replay feature on select channels (24h)' },
                { icon: 'ri-customer-service-2-line', text: 'Technical support 7 days/7, 10am–12am' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-3">
                  <i className={`${item.icon} text-primary flex-shrink-0`} />
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-white/50 italic">
              Channel counts and content availability are approximate and may vary. Onetvservices does not guarantee the continuous availability of any specific channel or title.
            </p>
          </section>

          {/* 2 — Content Disclaimer (NEW) */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(2)}
              Content Disclaimer — What Is &amp; Is Not Included
            </h2>

            <div className="space-y-4">
              {/* What IS included */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-green-400" />
                  What is included in your subscription
                </h3>
                <ul className="space-y-2">
                  {[
                    'Access to a curated library of live TV channels and VOD content via our IPTV platform.',
                    'Sports, news, entertainment, kids, and international channels from various regions.',
                    'Video-on-demand library including movies and series from various genres.',
                    'Replay functionality on select channels (up to 24 hours, where available).',
                    'Technical support and assistance with installation and setup.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <i className="ri-check-line text-green-400 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What is NOT included */}
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <i className="ri-close-circle-line text-red-400" />
                  What is NOT included in your subscription
                </h3>
                <ul className="space-y-2">
                  {[
                    'Official subscriptions to Netflix, Disney+, Amazon Prime Video, Apple TV+, or any other third-party streaming platform.',
                    'Official subscriptions to Canal+, beIN Sports, DAZN, Sky, or any other pay-TV broadcaster.',
                    'Any content that requires a separate paid subscription from its original provider.',
                    'Guaranteed availability of any specific channel, show, movie, or sporting event.',
                    'Rights to download, record, or redistribute any content accessed through our service.',
                    'Any hardware device (Smart TV, Fire Stick, Android Box, etc.).',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <i className="ri-close-line text-red-400 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Content availability note */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <i className="ri-information-line text-primary" />
                  Content Availability Notice
                </h3>
                <p className="text-sm text-white/70">
                  The content accessible through Onetvservices is subject to change at any time without prior notice. Specific channels or titles may be added, modified, or removed. Onetvservices makes no warranties regarding the availability, accuracy, or legality of any specific content in your country of residence. It is your responsibility to ensure that use of this service complies with the laws of your jurisdiction.
                </p>
              </div>
            </div>
          </section>

          {/* 3 — Eligibility & Account */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(3)}
              Eligibility &amp; Account
            </h2>
            <ul className="space-y-3">
              {[
                'You must be at least 18 years old to subscribe to our service.',
                'You are responsible for maintaining the confidentiality of your login credentials.',
                'You agree to provide accurate and complete information during registration.',
                'Each subscription is for personal, private, non-commercial use only and may not be shared or resold.',
                'You are responsible for all activity that occurs under your account.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-primary mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 4 — Subscription & Payment */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(4)}
              Subscription &amp; Payment
            </h2>
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">Subscription Plans</h3>
                <p className="text-sm">We offer 1-month, 3-month, 6-month, and 12-month subscription plans. Prices are displayed on our website and are subject to change with prior notice.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">Payment Methods</h3>
                <p className="text-sm">We accept Visa/Mastercard (with 10% discount), bank transfer, Wafacash, CashPlus, PayPal, Remitly, Western Union, Binance, and European bank accounts.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">Activation</h3>
                <p className="text-sm">Subscriptions are activated within 5 minutes of payment confirmation, 7 days a week from 10am to 12am. Activation outside these hours may be delayed to the next available support window.</p>
              </div>
            </div>
          </section>

          {/* 5 — Refund Policy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(5)}
              Refund Policy
            </h2>
            <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 border border-green-500/20 rounded-xl p-6 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-shield-check-line text-green-400 text-2xl" />
                <h3 className="font-bold text-white text-lg">7-Day Satisfaction Guarantee</h3>
              </div>
              <p className="text-white/70">
                If you are not fully satisfied within the first 7 days of your subscription, we will issue a full refund — no questions asked. This guarantee applies to first-time subscribers only.
              </p>
            </div>
            <ul className="space-y-2">
              {[
                'Refund requests must be submitted within 7 days of subscription activation.',
                'Refunds are processed within 5–10 business days via the original payment method.',
                'Refunds are not available after the 7-day period has elapsed.',
                'Refunds do not apply to renewals or second subscriptions.',
                'Abuse of the refund policy may result in account suspension.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-information-line text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm">
              For full details, see our <Link to="/refund" className="text-primary underline hover:text-primary/80 cursor-pointer">Refund Policy page</Link>.
            </p>
          </section>

          {/* 6 — Acceptable Use */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(6)}
              Acceptable Use Policy
            </h2>
            <p className="mb-4">By using our service, you agree <strong className="text-white">NOT</strong> to:</p>
            <ul className="space-y-2">
              {[
                'Share, resell, or redistribute your subscription credentials to third parties.',
                'Use the service for any commercial, public broadcasting, or public display purpose.',
                'Attempt to reverse-engineer, hack, or disrupt our platform or servers.',
                'Use the service in violation of any applicable laws or regulations in your country.',
                'Create multiple accounts to abuse the free trial or refund offer.',
                'Use automated tools, bots, or scrapers to access the service.',
                'Circumvent any geographic or technical restrictions imposed by the service.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 7 — Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(7)}
              Intellectual Property &amp; Content Rights
            </h2>
            <div className="space-y-4">
              <p>
                Onetvservices does <strong className="text-white">not</strong> own, produce, or hold exclusive rights to any of the television channels, movies, series, or other content accessible through our platform. All content remains the intellectual property of its respective owners, producers, and rights holders.
              </p>
              <p>
                Our service provides technical access to content streams. You acknowledge that:
              </p>
              <ul className="space-y-2">
                {[
                  'All content is protected by copyright and applicable intellectual property laws.',
                  'You may only use the content for personal, private, non-commercial viewing.',
                  'You may not copy, record, reproduce, distribute, or publicly display any content.',
                  'Onetvservices is not responsible for the content, accuracy, or legality of third-party streams.',
                  'Content availability is not guaranteed and may change without notice.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-information-line text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 8 — Service Availability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(8)}
              Service Availability
            </h2>
            <p className="mb-4">
              We strive to maintain high uptime using our Anti-Freeze technology and redundant server infrastructure. However:
            </p>
            <ul className="space-y-2">
              {[
                'We do not guarantee uninterrupted service at all times.',
                'Scheduled maintenance may temporarily affect service availability.',
                'Service quality depends on your internet connection speed and stability.',
                'We are not responsible for outages caused by third-party providers or infrastructure.',
                'Specific channels or content may become unavailable due to factors outside our control.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 9 — Account Termination */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(9)}
              Account Termination
            </h2>
            <p className="mb-4">
              We reserve the right to suspend or terminate your account without prior notice if:
            </p>
            <ul className="space-y-2">
              {[
                'You violate any of these Terms & Conditions.',
                'We detect fraudulent, abusive, or suspicious behaviour.',
                'You share your credentials with unauthorised users.',
                'Payment is not received, reversed, or charged back.',
                'You use the service for commercial or public broadcasting purposes.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 10 — Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(10)}
              Limitation of Liability
            </h2>
            <p className="mb-3">
              To the maximum extent permitted by applicable law, Onetvservices shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our service, including but not limited to:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                'Loss of access to specific channels or content.',
                'Interruptions, delays, or errors in the service.',
                'Any content viewed through our platform.',
                'Incompatibility with your device or internet connection.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-information-line text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm">
              Our total liability shall not exceed the amount paid by you for the current subscription period.
            </p>
          </section>

          {/* 11 — Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              {SECTION_NUM(11)}
              Changes to Terms
            </h2>
            <p>
              We reserve the right to modify these Terms &amp; Conditions at any time. Changes will be effective immediately upon posting to our website. Your continued use of the service after changes are posted constitutes your acceptance of the revised terms. We recommend reviewing these terms periodically.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-3">Questions?</h2>
            <p className="text-white/70 mb-6">
              If you have any questions about these Terms &amp; Conditions, please contact us:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:onetvservices@gmail.com"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-primary text-lg" />
                <span className="text-sm font-medium">onetvservices@gmail.com</span>
              </a>
              <a
                href="https://wa.me/12089746525"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-green-400 text-lg" />
                <span className="text-sm font-medium">WhatsApp Support</span>
              </a>
              <a
                href="tel:+12089746525"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line text-primary text-lg" />
                <span className="text-sm font-medium">+1 (208) 974 6525</span>
              </a>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8 px-4 text-center text-white/40 text-sm">
        <p>Onetvservices 2019-2026 © All Rights Reserved</p>
        <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
          <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
          <span>·</span>
          <Link to="/terms" className="text-primary cursor-pointer">Terms &amp; Conditions</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-white/70 transition-colors cursor-pointer">Privacy Policy</Link>
          <span>·</span>
          <Link to="/refund" className="hover:text-white/70 transition-colors cursor-pointer">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
}
